import { createReducer, Action, on } from '@ngrx/store';
import { SuiSortDirection as SortDirection } from '@semeris/ui';

import { TransactionActions } from './transaction.actions';
import { TransactionState } from './transaction.state';
import { Transaction } from '../../features/home/models/transaction.model';
import { arrayToFixedValueRecord } from '../../features/shared/utils/array-to-record';
import { measureReducer } from '../../features/shared/utils/runtime-reducer-stats';
import { EmbeddedActions } from '../embedded/embedded.actions';
import { RootStoreActions } from '../root-store.actions';

export const initialState: TransactionState = {
  loading: false,
};

const featureReducer = measureReducer(
  createReducer(
    initialState,
    on(TransactionActions.LoadTransactions, TransactionActions.UpsertTransaction, TransactionActions.DeleteTransaction, (state) => ({
      ...state,
      loading: true,
      error: undefined,
    })),
    // Asset/workspace switch re-scopes every request (X-Semeris-Asset header). Clear the list so
    // the parallel LoadTransactions repopulates from empty — otherwise the merge in
    // LoadTransactionsSuccess would keep the previous asset's transactions alongside the new ones.
    on(
      RootStoreActions.ChangeAsset,
      (state): TransactionState => ({ ...state, list: undefined, loaded: false, loading: true, error: undefined }),
    ),
    on(TransactionActions.LoadList, (state) => ({ ...state, loading: true, error: undefined })),
    on(TransactionActions.LoadTransactionsSuccess, (state, action): TransactionState => {
      if (!action.transactions.length) {
        return { ...state, loading: false, loaded: true };
      }

      const newIdLookup = arrayToFixedValueRecord(action.transactions, true, 'transactionId');

      const list = [...(state.list?.filter((t) => !newIdLookup[t.transactionId]) || []), ...action.transactions];

      const lastUpdatedAt = list.reduce((acc, t) => {
        return Math.max(acc, new Date(t.updatedAt || 0).getTime());
      }, 0);

      return {
        ...state,
        loading: false,
        loaded: true,
        list,
        lastUpdatedAt: new Date(lastUpdatedAt).toISOString(),
      };
    }),
    on(TransactionActions.LoadListSuccess, (state, action) => ({
      ...state,
      loading: false,
      loaded: true,
      list: action.list,
    })),
    on(EmbeddedActions.LoadDealSuccess, (state, action) => ({
      ...state,
      loading: false,
      loaded: true,
      list: [action.deal],
    })),
    on(
      TransactionActions.LoadTransactionsFail,
      TransactionActions.LoadListFail,
      TransactionActions.UpsertTransactionFail,
      TransactionActions.DeleteTransactionFail,
      (state, action) => ({ ...state, loading: false, error: action.error }),
    ),
    on(TransactionActions.Sort, (state, action) => ({ ...state, sort: action.sort })),
    on(TransactionActions.Select, (state, action) => ({
      ...state,
      selectedId: action.transactionId,
    })),
    on(TransactionActions.Search, (state, action) => ({
      ...state,
      search: action.search,
    })),
    on(TransactionActions.SetFilter, (state, action) => ({
      ...state,
      filter: action.filter,
    })),
    on(TransactionActions.ClearModifiers, (state) => ({
      ...state,
      sort: {
        active: 'transactionDisplayClosingDate',
        direction: 'desc' as SortDirection,
      },
      search: undefined,
      filter: {},
    })),
    on(TransactionActions.DeleteTransactionSuccess, (state, action) => ({
      ...state,
      loading: false,
      selectedId: state.selectedId === action.transaction.transactionId ? undefined : state.selectedId,
      list: state?.list?.filter((t) => t.transactionId !== action.transaction.transactionId),
    })),
    on(TransactionActions.UpsertTransactionSuccess, (state, action) => ({
      ...state,
      loading: false,
      selectedId: action.transaction.transactionId,
      list: [...(state?.list || []).filter((t) => t.transactionId !== action.transaction.transactionId), action.transaction],
    })),
    on(TransactionActions.PollingTransactionChangesSuccess, (state, action) => {
      if (!action.transactions.length) {
        // Do not update state if no transactions are returned
        return state;
      }

      const listLookup = new Map<string, Transaction>([...(state?.list || [])].map((t): [string, Transaction] => [t.transactionId, t]));

      const updatedListLookup = action.transactions.reduce((acc, t) => {
        acc.set(t.transactionId, t);
        return acc;
      }, listLookup);

      const list = Array.from(updatedListLookup.values());

      const lastUpdatedAt = list.reduce((acc, t) => {
        return Math.max(acc, new Date(t.updatedAt || 0).getTime());
      }, 0);

      return {
        ...state,
        list,
        lastUpdatedAt: new Date(lastUpdatedAt).toISOString(),
      };
    }),
    on(TransactionActions.FullRefreshTransactionsSuccess, (state, action): TransactionState => {
      if (!action.transactions.length) {
        // Never wipe the list on an empty full-refresh response — keep the current state
        return state;
      }

      const lastUpdatedAt = action.transactions.reduce((acc, t) => {
        return Math.max(acc, new Date(t.updatedAt || 0).getTime());
      }, 0);

      return {
        ...state,
        list: action.transactions,
        lastUpdatedAt: new Date(lastUpdatedAt).toISOString(),
      };
    }),
  ),
);

export function reducer(state: TransactionState | undefined, action: Action) {
  return featureReducer(state, action);
}
